import type {
  ConsoleConversationTrace,
  ConsoleTraceMessage,
  ConsoleTraceRun,
  ConversationId,
  MessageId,
} from '@plato/shared';
import { useCallback, useState } from 'react';
import { ActionError, useAction } from '../../components/action.tsx';
import { formatDateTime } from '../../components/format.ts';
import { Loaded } from '../../components/page.tsx';
import { useConsole, useHasRole, useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import { MessageParts } from './message-parts.tsx';
import { RunTrace } from './run-trace.tsx';

export function ConversationDetail(props: { conversationId: ConversationId; userId: string }) {
  const { conversationId, userId } = props;
  const load = useCallback(
    (data: ConsoleData, tenant: TenantSummary) =>
      data.getConversationTrace(tenant.id, conversationId, userId),
    [conversationId, userId],
  );
  const { query } = useReloadableTenantQuery(load);
  return (
    <>
      <p>
        <a href="#/conversations">← All conversations</a>
      </p>
      <Loaded query={query}>{(trace) => <Transcript trace={trace} userId={userId} />}</Loaded>
    </>
  );
}

function Transcript({ trace, userId }: { trace: ConsoleConversationTrace; userId: string }) {
  const { conversation } = trace;
  const runsByReply = new Map(trace.runs.map((run) => [run.reply_message_id, run]));
  const runsWithoutReply = trace.runs.filter((run) => run.reply_message_id === null);
  return (
    <>
      <h1 id="page-title">{conversation.title ?? 'Untitled conversation'}</h1>
      <p className="summary">
        Started {formatDateTime(conversation.created_at)}, last activity{' '}
        {formatDateTime(conversation.updated_at)}.{' '}
        {conversation.purpose !== 'chat' && (
          <span className="chip chip-warning">{purposeLabel(conversation.purpose)}</span>
        )}
      </p>
      <ol className="transcript" aria-label="Transcript">
        {trace.messages.map((message) => (
          <li key={message.id} className={`turn turn-${message.role}`}>
            <TranscriptMessage
              message={message}
              trace={trace}
              userId={userId}
              run={runsByReply.get(message.id)}
            />
          </li>
        ))}
      </ol>
      {runsWithoutReply.length > 0 && (
        <section aria-labelledby="unanswered">
          <h2 id="unanswered">Replies that were not stored</h2>
          {runsWithoutReply.map((run) => (
            <RunTrace key={run.id} run={run} />
          ))}
        </section>
      )}
    </>
  );
}

function TranscriptMessage(props: {
  message: ConsoleTraceMessage;
  trace: ConsoleConversationTrace;
  userId: string;
  run: ConsoleTraceRun | undefined;
}) {
  const { message, trace, run } = props;
  const feedback = trace.feedback.find((row) => row.message_id === message.id);
  return (
    <article aria-label={message.role === 'user' ? 'User message' : 'Assistant reply'}>
      <header className="turn-header">
        <strong>{message.role === 'user' ? 'User' : 'Assistant'}</strong>
        <span className="muted">{formatDateTime(message.created_at)}</span>
        {message.model_id !== null && <code className="muted">{message.model_id}</code>}
      </header>
      <MessageParts parts={message.parts} />
      {feedback && (
        <p className={`notice ${feedback.rating === 'down' ? 'notice-danger' : 'notice-success'}`}>
          <strong>Rated {feedback.rating === 'down' ? 'down' : 'up'}</strong>
          {feedback.comment !== null && <>: “{feedback.comment}”</>}
        </p>
      )}
      {message.role === 'assistant' && (
        <SaveAsEvalCase
          conversationId={trace.conversation.id}
          replyId={message.id}
          userId={props.userId}
        />
      )}
      {run && <RunTrace run={run} />}
    </article>
  );
}

function SaveAsEvalCase(props: {
  conversationId: ConversationId;
  replyId: MessageId;
  userId: string;
}) {
  const { data, tenant } = useConsole();
  const canEdit = useHasRole('editor');
  const action = useAction();
  const [saved, setSaved] = useState(false);
  if (!canEdit) return null;
  if (saved) {
    return (
      <p className="notice notice-success" role="status">
        Saved as an eval case. <a href="#/evaluations">Write its rubric in Evaluations</a>.
      </p>
    );
  }
  const save = () =>
    void action.run(async () => {
      await data.saveReplyAsEvalCase(tenant.id, props.conversationId, props.replyId, props.userId);
      setSaved(true);
    });
  return (
    <>
      <button type="button" className="quiet" onClick={save} disabled={action.pending}>
        Save as eval case
      </button>
      <ActionError error={action.error} />
    </>
  );
}

function purposeLabel(purpose: string): string {
  if (purpose === 'eval') return 'Eval trial';
  if (purpose === 'preview') return 'Try-it chat';
  return purpose;
}
